import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { List, X, ShoppingBag } from "@phosphor-icons/react";
import { useCart } from "../context/CartContext";
import { scrollToId } from "../lib/scroll";

const NAV = [
  { label: "Historia", id: "historia" },
  { label: "El producto", id: "producto" },
  { label: "Colecciones", id: "colecciones" },
  { label: "Sobre mí", id: "sobre-mi" },
  { label: "Blog", to: "/blog" },
  { label: "Contacto", id: "contacto" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (item) => {
    setMenuOpen(false);
    if (item.to) {
      navigate(item.to);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: item.id } });
    } else {
      scrollToId(item.id);
    }
  };

  const goHome = () => {
    setMenuOpen(false);
    if (location.pathname !== "/") navigate("/");
    else scrollToId("top");
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled || menuOpen
          ? "bg-white/85 backdrop-blur-md border-b border-[#EBEBEB] py-3"
          : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        <button data-testid="logo-btn" onClick={goHome} className="text-left leading-none">
          <span className="font-display text-2xl md:text-[1.7rem] text-[#2D3732] tracking-tight">
            VITAE
          </span>
          <span className="block overline mt-0.5 text-[0.55rem]">by Ana</span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {NAV.map((item) => (
            <button
              key={item.label}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => go(item)}
              className="link-underline text-sm text-[#4A554E] hover:text-[#2D3732] transition-colors"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative p-2 text-[#2D3732] hover:text-[#8A9A6F] transition-colors"
            aria-label="Abrir carrito"
          >
            <ShoppingBag size={24} weight="light" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-0.5 -right-0.5 bg-[#8A9A6F] text-white text-[0.6rem] w-4 h-4 rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </button>
          <button
            className="lg:hidden p-2 text-[#2D3732]"
            data-testid="menu-toggle"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Menú"
          >
            {menuOpen ? <X size={24} weight="light" /> : <List size={24} weight="light" />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div data-testid="mobile-menu" className="lg:hidden mt-3 px-6 pb-4 flex flex-col gap-1">
          {NAV.map((item) => (
            <button
              key={item.label}
              onClick={() => go(item)}
              data-testid={`mnav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-left py-3 border-b border-[#EBEBEB] text-[#2D3732] font-display text-xl"
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
}
